import { Weaken } from 'utils';
import { NodeRow, BetweenNodesRow } from '../types';
import { PCNode } from '../../MineModel/Nodes';
import { buildNodeSpatialHash } from './spatialHash';

export interface NodeValidationResult {
  isValid: boolean;
  errors: string[];
  duplicateIds?: string[];
  duplicateCoords?: { nodeId: string; existingNodeId: string }[];
  nodesToRemove?: string[];
  // A node dropped for sharing coordinates with an existing one hands its edges over: every spool that
  // touched the dropped node is re-pointed at `keepNodeId`. Edges are spools now, so this is the whole
  // of what a merge has to carry.
  spoolsToRepoint?: { dropNodeId: string; keepNodeId: string }[];
}

interface Coordinates {
  x: number;
  y: number;
  z: number;
}

export const validateNodeIds = (nodes: PCNode[]): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  nodes.forEach((node) => {
    if (seen.has(node.id)) {
      duplicates.add(node.id);
    } else {
      seen.add(node.id);
    }
  });
  return Array.from(duplicates);
};

const areCoordinatesEqual = (coord1: Coordinates, coord2: Coordinates, tolerance: number = 0.5): boolean => {
  const dx = Math.abs(coord1.x - coord2.x);
  const dy = Math.abs(coord1.y - coord2.y);
  const dz = Math.abs(coord1.z - coord2.z);
  return dx <= tolerance && dy <= tolerance && dz <= tolerance;
};

export const validateNodeCoordinates = (newNode: PCNode, existingNodes: PCNode[], tolerance: number = 0.5): NodeValidationResult => {
  const result: NodeValidationResult = {
    isValid: true,
    errors: [],
    duplicateCoords: [],
    nodesToRemove: [],
    spoolsToRepoint: [],
  };

  // Check for duplicate IDs
  if (existingNodes.some((node) => node.id === newNode.id)) {
    result.isValid = false;
    result.errors.push(`Node ID ${newNode.id} already exists`);
  }

  // Check for duplicate coordinates
  const duplicateNodes = existingNodes.filter((node) => areCoordinatesEqual(node.coords, newNode.coords, tolerance));

  if (duplicateNodes.length > 0) {
    result.isValid = false;
    const existingNode = duplicateNodes[0];
    result.duplicateCoords?.push({
      nodeId: newNode.id,
      existingNodeId: existingNode.id,
    });
    result.nodesToRemove?.push(newNode.id);
    result.spoolsToRepoint?.push({ dropNodeId: newNode.id, keepNodeId: existingNode.id });
  }

  return result;
};

// generates list of non valid nodes for dxf purposes so they can be changed/renamed
export const validateNodes = (newNodes: PCNode[], existingNodes: PCNode[], tolerance: number = 0.5): NodeValidationResult => {
  const result: NodeValidationResult = {
    isValid: true,
    errors: [],
    duplicateIds: [],
    duplicateCoords: [],
    nodesToRemove: [],
    spoolsToRepoint: [],
  };

  const duplicateIds = validateNodeIds(newNodes);
  if (duplicateIds.length > 0) {
    result.isValid = false;
    result.errors.push('Duplicate node IDs found in new nodes');
    result.duplicateIds = duplicateIds;
    return result;
  }

  const existingIdSet = new Set(existingNodes.map((n) => n.id));
  const existingHash = buildNodeSpatialHash(existingNodes, Math.max(tolerance * 2, 1.0));

  newNodes.forEach((newNode) => {
    if (existingIdSet.has(newNode.id)) {
      result.isValid = false;
      result.errors.push(`Node ID ${newNode.id} already exists`);
    }

    const match = existingHash.findNearest(newNode.coords.x, newNode.coords.y, newNode.coords.z, tolerance);
    if (match) {
      result.isValid = false;
      result.duplicateCoords?.push({ nodeId: newNode.id, existingNodeId: match.id });
      result.nodesToRemove?.push(newNode.id);
      result.spoolsToRepoint?.push({ dropNodeId: newNode.id, keepNodeId: match.id });
    }
  });

  return result;
};

// checks for duplicate coordinates and ids when comaped to existing data
export const validateNodeRowCoordinates = (row: NodeRow | BetweenNodesRow, existingNodes: PCNode[], tolerance: number = 0.5): NodeValidationResult => {
  const result: NodeValidationResult = {
    isValid: true,
    errors: [],
    duplicateCoords: [],
  };

  // Check for duplicate IDs
  if (existingNodes.some((node) => node.id === row.nodeId)) {
    result.isValid = false;
    result.errors.push(`Node ID ${row.nodeId} already exists`);
    return result;
  }

  // Check for duplicate coordinates
  const newCoords: Coordinates = {
    x: Number(row.x),
    y: Number(row.y),
    z: Number(row.z),
  };

  const duplicateNodes = existingNodes.filter((node) => areCoordinatesEqual(node.coords, newCoords, tolerance));

  if (duplicateNodes.length > 0) {
    const existingNode = duplicateNodes[0];
    result.errors.push(`Node ID ${row.nodeId} shares the same coordinates as Node ID ${existingNode.id}`);
    result.isValid = false;
    result.duplicateCoords?.push({
      nodeId: row.nodeId,
      existingNodeId: existingNode.id,
    });
  }

  return result;
};

// further checks that the node before and after are connected
export const validateBetweenNodesRow = (row: BetweenNodesRow, existingNodes: PCNode[], tolerance: number = 0.5): NodeValidationResult => {
  // First do the basic node validation
  const result = validateNodeRowCoordinates(row, existingNodes, tolerance);

  // If basic validation failed, return early
  if (!result.isValid) {
    return result;
  }

  // Check for duplicate nodeId
  const hasSameNodeId = existingNodes.some((node) => node.id === row.nodeId);

  if (hasSameNodeId) {
    result.isValid = false;
    result.errors.push(`Node '${row.nodeId}' is already defined.`);
  }

  return result;
};

// checks for duplicate ids and coordinates when compared to itself
export const validateNodesUniqueness = (nodes: Weaken<NodeRow>[], tolerance: number = 0.5): NodeValidationResult => {
  const result: NodeValidationResult = {
    isValid: true,
    errors: [],
    duplicateIds: [],
    duplicateCoords: [],
  };

  // Check for duplicate IDs
  const nodeIds = new Map<string, number>();
  nodes.forEach((node, index) => {
    if (node.nodeId) {
      if (nodeIds.has(node.nodeId)) {
        result.isValid = false;
        result.errors.push(`Duplicate Node ID: ${node.nodeId}`);
        result.duplicateIds?.push(node.nodeId);
      } else {
        nodeIds.set(node.nodeId, index);
      }
    }
  });

  // Check for duplicate coordinates
  for (let i = 0; i < nodes.length; i += 1) {
    const node1 = nodes[i];
    const coords1: Coordinates = {
      x: Number(node1.x),
      y: Number(node1.y),
      z: Number(node1.z),
    };

    for (let j = i + 1; j < nodes.length; j += 1) {
      const node2 = nodes[j];
      const coords2: Coordinates = {
        x: Number(node2.x),
        y: Number(node2.y),
        z: Number(node2.z),
      };

      if (areCoordinatesEqual(coords1, coords2, tolerance)) {
        result.isValid = false;
        result.errors.push(`Nodes ${node1.nodeId} and ${node2.nodeId} share the same coordinates`);
        result.duplicateCoords?.push({
          nodeId: node1.nodeId || '',
          existingNodeId: node2.nodeId || '',
        });
      }
    }
  }

  return result;
};
